import React, { useCallback, useRef, useState } from 'react'

import afterFrame from 'afterframe'
import cx from 'classnames'
import { InboxApi } from 'conversations/api'
import ContextMenu, { ContextMenuActions } from 'conversations/components/ContextMenu'
import { AvatarWithOnlineIndicator } from 'conversations/components/ConversationAvatar'
import CQLScore from 'conversations/components/CQLScore'
import Timer from 'conversations/components/Timer'
import { BadgeCounts } from 'conversations/hooks/useBadgeCounts'
import { useEndUser } from 'conversations/hooks/useEndUser'
import { useFetchConversationMessages } from 'conversations/hooks/useFetchConversationMessages'
import { useSectionConfigProperty } from 'conversations/hooks/useSections'
import { useToast } from 'conversations/hooks/useToastNotification'
import { getBadgeCounts } from 'conversations/modules/conversation/selectors'
import { getLocation } from 'conversations/modules/endUser/selectors'
import { hasConversationResponseTimer } from 'conversations/modules/gates/selectors'
import { formatGeolocation } from 'conversations/utils/GeoUtils'
import { measureInteraction } from 'conversations/utils/MetricUtil'
import { getDisplayName } from 'conversations/utils/nameUtils'
import { Pill } from 'conversations/utils/pillUtil'
import { timeSinceUpdated } from 'conversations/utils/timeStamp'
import { useDispatch, useSelector } from 'react-redux'
import { receiveManuallyMarkedAsRead } from 'shared/store/actions/conversation'

import styles from './styles.module.css'
import utilityStyles from 'conversations/css/utility.module.css'

export type ItemKey = string | number

interface Props {
  itemKey: ItemKey
  inboxId?: number
  sectionID: string
  onSelect: (key: ItemKey) => void
  selected: boolean
  selectedStatus: Pill
  newContent: boolean
  endUserId?: string
  timestamp?: number
  responseTimerDuration: number | null
  details?: React.ReactNode
  action?: React.ReactNode
  testId?: string
}

const BaseListItem: React.FC<Props> = ({
  itemKey,
  inboxId,
  sectionID,
  endUserId,
  selected,
  selectedStatus,
  newContent,
  timestamp,
  responseTimerDuration,
  details,
  action,
  onSelect,
  testId,
}): JSX.Element => {
  const endUser = useEndUser(endUserId)
  const convoItemRef = useRef(null)
  const toast = useToast()
  const dispatch = useDispatch()
  const [contextMenuActive, setContextMenuActive] = useState(false)
  const timeDiff = timestamp && timeSinceUpdated(timestamp)
  const geolocation = getLocation(endUser)
  const sectionType = useSectionConfigProperty(sectionID, 'type')
  const isMyConvosSection = sectionType === 'my-convos'
  // TODO: remove newContent check below when conversationUnread endpoint is created
  const showContextMenu = isMyConvosSection && newContent
  const locationOrCompany =
    endUser?.endUserEnrichment?.company?.data?.name ?? formatGeolocation(geolocation)
  const score = endUser?.attributes?._cql_score
  const canAction = !!action

  const handleItemSelect = useCallback(() => {
    const interaction = measureInteraction('drift_convo_desktop_open_convo_render_time', true)
    afterFrame(() => {
      interaction.end()
    })
    onSelect(itemKey)
  }, [itemKey, onSelect])
  const isContextReadAction = newContent
  const conversationId = typeof itemKey === 'string' ? parseInt(itemKey) : itemKey
  const prevBadgeCounts: BadgeCounts = useSelector(getBadgeCounts())
  const hasResponseTimer = useSelector(hasConversationResponseTimer)

  const shouldFetchOrShowTimer = hasResponseTimer && isMyConvosSection && selectedStatus === 'open'
  useFetchConversationMessages(conversationId, shouldFetchOrShowTimer)

  const readConversation = async () => {
    dispatch(receiveManuallyMarkedAsRead({ conversationId, inboxId, prevBadgeCounts }))
    try {
      await InboxApi.post(`/conversations/${conversationId}/conversationRead`)
    } catch (err) {
      toast({ body: 'Unable to read conversation' })
    }
  }

  const handleContextMenu = useCallback((val) => setContextMenuActive(val), [])

  const contextActions: ContextMenuActions[] = [
    {
      label: `Mark as ${isContextReadAction ? 'read' : 'unread'}`,
      onClick: (e) => {
        e.stopPropagation()
        isContextReadAction && readConversation()
      },
      dataOcwebId: 'ConversationContextActionRead',
    },
  ]

  return (
    <div
      key={itemKey}
      ref={convoItemRef}
      data-ocweb-id={testId}
      onClick={handleItemSelect}
      className={cx(styles.baseListItem, {
        [styles.selected]: selected,
        [styles.expandableSectionItem]: !selected,
        [styles.canAction]: canAction,
        [styles.contextActive]: contextMenuActive,
        [styles.timerActive]: shouldFetchOrShowTimer && responseTimerDuration,
      })}>
      {showContextMenu && (
        <ContextMenu
          outerRef={convoItemRef}
          actions={contextActions}
          setContextMenuActive={handleContextMenu}
        />
      )}
      <AvatarWithOnlineIndicator user={endUser} size={'mini'} />
      <div>
        <div className={styles.contentAndXContainer}>
          <div className={styles.contentContainer}>
            <div className={styles.endUserPreview}>
              <div className={styles.userNameContainer}>
                <div
                  data-private
                  className={cx(
                    styles.userName,
                    utilityStyles.mdFontSize,
                    utilityStyles.boldWeight,
                    utilityStyles.blackColor
                  )}>
                  {getDisplayName(endUser)}
                </div>
              </div>
              <CQLScore score={score} />
            </div>
            <div
              className={cx(utilityStyles.smFontSize, {
                [utilityStyles.semiBoldWeight]: newContent,
                [utilityStyles.blueColor]: newContent,
                [utilityStyles.blackColor]: !newContent,
              })}>
              {details}
            </div>
            <div className={cx(utilityStyles.smFontSize, styles.endUserLocation)}>
              {locationOrCompany}
            </div>
          </div>
          <div
            className={cx(utilityStyles.smFontSize, styles.timeDiff, {
              [utilityStyles.blackColor]: !newContent,
              [utilityStyles.blueColor]: newContent,
            })}>
            {timeDiff}
          </div>
          <div className={styles.itemAction}>{action}</div>
        </div>
        {shouldFetchOrShowTimer && responseTimerDuration && (
          <div
            data-ocweb-id={`responseTimer-${conversationId}`}
            className={cx(styles.contentAndXContainer, styles.responseTimerContainer)}>
            <div>
              <div className={cx(utilityStyles.smFontSize, styles.responseTimeText)}>
                {'Visitor waiting for '}
                <Timer initialDuration={responseTimerDuration} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default BaseListItem
